'use client';

import { useEffect, useState } from 'react';
import { SpeakerWaveIcon } from '@heroicons/react/24/outline';

/**
 * Tiruan layar antrian yang tampil di TV ruang tunggu — murni dekoratif,
 * tidak menyentuh API. Nomor bergeser tiap beberapa detik supaya pengunjung
 * landing langsung paham cara kerjanya.
 */

const SKENARIO = [
  { loket: [{ no: '012', status: 'MEMANGGIL' }, { no: '011', status: 'DILAYANI' }], menunggu: ['013', '014', '015', '016'] },
  { loket: [{ no: '013', status: 'DILAYANI' }, { no: '014', status: 'MEMANGGIL' }], menunggu: ['015', '016', '017', '018'] },
  { loket: [{ no: '015', status: 'MEMANGGIL' }, { no: '014', status: 'DILAYANI' }], menunggu: ['016', '017', '018', '019'] },
];

export default function QueuePreview({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % SKENARIO.length), 2800);
    return () => clearInterval(t);
  }, []);

  const data = SKENARIO[i];
  const big = size === 'lg';

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl ring-1 ring-white/10">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          Antrian Servis
        </span>
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
          Live
        </span>
      </div>

      <div className={`grid gap-3 p-4 ${big ? 'sm:grid-cols-3' : 'grid-cols-3'}`}>
        <div className={`space-y-3 ${big ? 'sm:col-span-2' : 'col-span-2'}`}>
          {data.loket.map((l, idx) => {
            const memanggil = l.status === 'MEMANGGIL';
            return (
              <div
                key={idx}
                className={`rounded-xl border px-4 py-3 ${
                  memanggil
                    ? 'border-amber-400/40 bg-amber-400/10'
                    : 'border-white/10 bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-slate-400">Loket {idx + 1}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[9px] font-bold tracking-wide ${
                      memanggil
                        ? 'animate-pulse bg-amber-400 text-slate-900'
                        : 'bg-indigo-500/80 text-white'
                    }`}
                  >
                    {l.status}
                  </span>
                </div>
                <p
                  key={l.no}
                  className={`animate-queue-pop font-bold tabular-nums text-white ${
                    big ? 'text-4xl sm:text-6xl' : 'text-3xl'
                  }`}
                >
                  {l.no}
                </p>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Menunggu
          </p>
          <ul className="mt-2 space-y-1.5">
            {data.menunggu.map((n) => (
              <li
                key={n}
                className={`rounded-lg bg-white/5 py-1 text-center font-semibold tabular-nums text-slate-200 ${
                  big ? 'text-lg' : 'text-sm'
                }`}
              >
                {n}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-white/10 px-4 py-2.5 text-[11px] text-slate-400">
        <SpeakerWaveIcon className="h-4 w-4 text-amber-400" aria-hidden="true" />
        <span className="truncate">
          &ldquo;Nomor antrian {data.loket.find((l) => l.status === 'MEMANGGIL')?.no}, silakan
          menuju loket&rdquo;
        </span>
      </div>
    </div>
  );
}
